import React, { createContext, useContext, useState } from "react";
import { ProbateField } from "./models/ProbateField";
import { LeadContext } from "./LeadContextWrapper";
import { probateApi } from "apis";
import { ProbateDetails } from "models/ProbateDetails";
import { isProd } from "./constants";
 
export const knowledgeEngineDataPrefix = "knowledge_engine_"

interface Props {
  children: JSX.Element;
}

export interface LeadProbateContextInterface {
  saveFieldAnswered?: (field: ProbateField) => Promise<void>;
  getFieldsAnswered?: () => Promise<ProbateField[] | undefined>;
  clearFieldsAnswered?: () => void;
  getProbateDetails?: () => Promise<ProbateDetails|undefined>;
}

export const LeadProbateContext = createContext<LeadProbateContextInterface>({
  saveFieldAnswered: async () => undefined,
  getFieldsAnswered: async () => undefined,
  clearFieldsAnswered: () => undefined,
  getProbateDetails: async () => undefined,
});

export const LeadProbateContextWrapper = ({ children }: Props): JSX.Element => {
  const { lead, leadData, saveLeadData } = useContext(LeadContext);
  const [fieldsAnswered, setFieldsAnswered] = useState<ProbateField[]>([]);

  const getProbateDetails = async (): Promise<ProbateDetails|undefined> => {
    if(!leadData) {
      !isProd && console.warn("we need lead data before we can get probate details/fields")
      return
    }

    console.log("getting probate details")

    const fieldsAnswered:ProbateField[] = await getFieldsAnswered() || []
    const probateDetailResponse = await probateApi.getProbateDetailsForLead(leadData["country_id"] , leadData["state_id"], fieldsAnswered);
    if(probateDetailResponse) {
      probateDetailResponse.injectPreviouslyAnsweredFromLeadData(leadData)
    }

    return probateDetailResponse
  };


  const pullKnowledgeDataFromAllData = (leadData: {[key:string]: any}): {[key:string]: any} => {
    console.log("pulling knowledge engine data")

    const keys = Object.keys(leadData)
    const myMap:{[key:string]: any} = {}
    keys.map((k:string) => {
      if(k.startsWith(knowledgeEngineDataPrefix)) {
        try {
          const val = typeof leadData[k] === "object" ? leadData[k] : JSON.parse(leadData[k])
          myMap[k.replace(knowledgeEngineDataPrefix, "")] = val
        } catch(e) {
          !isProd && console.warn("couldn't parse value for field " + k + " " + (e as any)?.message)
          try {
            const d = new Date(Date.parse(leadData[k]))
            myMap[k.replace(knowledgeEngineDataPrefix, "")] = d.toISOString()
          } catch(e) {
            !isProd  && console.warn("couldn't parse value for field as date either, " + k + " " + (e as any)?.message)
          }
        }
      }
    })

    return myMap
  }

  const getFieldsAnswered = async (savedFieldsAnswered = false): Promise<ProbateField[]> => {
    if(!lead?.key) {
      !isProd && console.warn("unable to pull probate fields because we are missing lead.key")
      return []
    }
    if(!leadData) {
      !isProd && console.warn("unable to pull probate fields because we are missing lead data")
      return []
    }

    console.log("getting answered fields")

    let fields: ProbateField[] = []
    try {
      const knowledgeEngineFields = pullKnowledgeDataFromAllData(leadData)
      fields = Object.values(knowledgeEngineFields)
      if (savedFieldsAnswered) {
        setFieldsAnswered(fields);
      }
    } catch(e) {
      console.error("unable to pull knowledge engine fields for lead " + lead.key + " " + (e as any)?.message)
    }

    return fields;
  }

  const saveFieldAnswered = async (probateField: ProbateField): Promise<void> => {
    if(!lead || !lead.key || !saveLeadData || !leadData) {
      throw new Error("lead context is not done loading yet")
    }

    console.log("saving answered fields")

    const foundFieldIndex = fieldsAnswered.findIndex((answeredField: ProbateField) => answeredField.id === probateField.id);
    let newFieldsAnswered:ProbateField[] = fieldsAnswered
    if(foundFieldIndex !== -1) {
      newFieldsAnswered[foundFieldIndex] = probateField 
    } else {
      newFieldsAnswered = [...newFieldsAnswered, probateField]
    }

    newFieldsAnswered.map(field => {
      if(!field.id) {
        console.error("field is missing id " + JSON.stringify(field))
        return
      }
      leadData[knowledgeEngineDataPrefix + field.id] = JSON.stringify(field)
    })

    await saveLeadData(lead.key, leadData)
    setFieldsAnswered(newFieldsAnswered);
  }

  const clearFieldsAnswered = async (clearStorage = true): Promise<void> => {
    console.log("clearing answered fields")

    if(clearStorage && leadData && lead?.key && saveLeadData) {
      const newData:{[key:string]:any} = {}
      Object.keys(leadData).map(k => {
        if(!k.startsWith(knowledgeEngineDataPrefix)) {
          newData[k] = leadData[k]
        }
      })

      await saveLeadData(lead.key, newData)
    }

    setFieldsAnswered([]);
  };

  return (
    <LeadProbateContext.Provider
      value={{
        saveFieldAnswered,
        getFieldsAnswered,
        clearFieldsAnswered,
        getProbateDetails,
      }}
    >
      {children}
    </LeadProbateContext.Provider>
  );
};
